import type { ApiNode } from '@goalbuddy/client';
import { canHaveParent, type NodeType } from '@goalbuddy/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Archive, Check, ChevronDown, ExternalLink, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  buttonVariants,
  Input,
  Label,
  Select,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@/components/ui';
import { api } from '@/lib/api';
import { useDetail, useDone, useNode, useReopen, useStructure, useUpdateNode, useUsers } from '@/lib/hooks';
import { ancestorsOf } from '@/lib/tree';
import { fmtDateTime } from '@/lib/utils';
import { ActivityTimeline } from './activity-timeline';
import { TypeIcon } from './node-meta';
import { NoteEditor } from './note-editor';
import { RoutineTracker } from './routine-tracker';

type Patch = Parameters<typeof api.updateNode>[1];

function Section({ title, children, open = false }: { title: string; children: ReactNode; open?: boolean }) {
  return (
    <details open={open} className="group/sec rounded-xl border border-border bg-surface">
      <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-semibold hover:bg-muted/60 [&::-webkit-details-marker]:hidden">
        {title}
        <ChevronDown size={15} className="text-muted-fg transition-transform group-open/sec:rotate-180" />
      </summary>
      <div className="grid grid-cols-1 gap-3 border-t border-border p-3.5 sm:grid-cols-2">{children}</div>
    </details>
  );
}

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

/** Text input that saves on blur when the value changed. `null` clears the field. */
function TextField({
  value,
  onSave,
  type = 'text',
  multiline,
  placeholder,
}: {
  value: string | number | null;
  onSave: (v: string | null) => void;
  type?: string;
  multiline?: boolean;
  placeholder?: string;
}) {
  const [v, setV] = useState(value === null ? '' : String(value));
  const commit = () => {
    const next = v.trim() === '' ? null : v.trim();
    if (next !== (value === null ? null : String(value))) onSave(next);
  };
  return multiline ? (
    <Textarea
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      placeholder={placeholder}
      rows={2}
    />
  ) : (
    <Input
      value={v}
      type={type}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && commit()}
      placeholder={placeholder}
    />
  );
}

const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
const fromLocalInput = (v: string | null) => (v ? new Date(v).toISOString() : null);

function TagsEditor({ node }: { node: ApiNode }) {
  const [input, setInput] = useState('');
  const add = useMutation({
    mutationFn: (tags: string[]) => api.addTags(node.id, tags),
    onSuccess: () => setInput(''),
  });
  const remove = useMutation({ mutationFn: (tag: string) => api.removeTag(node.id, tag) });
  return (
    <div className="flex flex-wrap items-center gap-1">
      {node.tags.map((t) => (
        <Badge key={t}>
          #{t}
          <button aria-label={`Remove ${t}`} onClick={() => remove.mutate(t)}>
            <X size={10} />
          </button>
        </Badge>
      ))}
      <Input
        value={input}
        placeholder="add tag…"
        className="h-7 w-28"
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && input.trim()) add.mutate(input.split(/[\s,;]+/).filter(Boolean));
        }}
      />
    </div>
  );
}

function NodeForm({ node }: { node: ApiNode }) {
  const update = useUpdateNode();
  const { data: users } = useUsers();
  const { data: structure } = useStructure();
  const save = (patch: Patch) => update.mutate({ id: node.id, ...patch });
  const buddy = useMutation({
    mutationFn: (on: boolean) => api.setBuddy(node.id, { buddy_focus: on }),
    onSuccess: (r) => r.warning && toast.warning(r.warning),
  });
  const move = useMutation({ mutationFn: (parentId: number | null) => api.moveNode(node.id, parentId) });
  const parentOptions = (structure ?? []).filter(
    (p) => p.id !== node.id && canHaveParent(node.type, p.type as NodeType),
  );
  const allowsNoParent = canHaveParent(node.type, null);

  return (
    <div className="space-y-3" key={node.id + node.updated_at}>
      <Section title="Basics" open>
        <Field label="Short description" wide>
          <TextField
            value={node.description_short}
            multiline
            onSave={(v) => save({ description_short: v })}
          />
        </Field>
        <Field label="Parent">
          <Select
            value={node.parent_id ?? ''}
            onChange={(e) => move.mutate(e.target.value ? Number(e.target.value) : null)}
          >
            {allowsNoParent && <option value="">(none / inbox)</option>}
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.type}: {p.title}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Priority">
          <Select
            value={node.priority ?? ''}
            onChange={(e) =>
              save({ priority: e.target.value ? (Number(e.target.value) as 1 | 2 | 3) : null })
            }
          >
            <option value="">–</option>
            <option value="1">1 (high)</option>
            <option value="2">2</option>
            <option value="3">3 (low)</option>
          </Select>
        </Field>
        <Field label="Energy">
          <Select
            value={node.energy ?? ''}
            onChange={(e) => save({ energy: (e.target.value || null) as ApiNode['energy'] })}
          >
            <option value="">–</option>
            <option value="low">low</option>
            <option value="med">med</option>
            <option value="high">high</option>
          </Select>
        </Field>
        <Field label="Context">
          <TextField value={node.context} placeholder="@computer" onSave={(v) => save({ context: v })} />
        </Field>
        <Field label="Effort (min)">
          <TextField
            value={node.effort_min}
            type="number"
            onSave={(v) => save({ effort_min: v === null ? null : Number(v) })}
          />
        </Field>
        <Field label="Period">
          <TextField
            value={node.period}
            placeholder="2026-Q4 or ongoing"
            onSave={(v) => save({ period: v })}
          />
        </Field>
        <Field label="Next action">
          <label className="flex h-9 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={node.is_next_action}
              onChange={(e) => save({ is_next_action: e.target.checked })}
            />{' '}
            Show in next actions
          </label>
        </Field>
        <Field label="Tags" wide>
          <TagsEditor node={node} />
        </Field>
      </Section>

      <Section title="Scheduling" open>
        <Field label="Start date">
          <TextField value={node.start_date} type="date" onSave={(v) => save({ start_date: v })} />
        </Field>
        <Field label="Due date">
          <TextField value={node.due_date} type="date" onSave={(v) => save({ due_date: v })} />
        </Field>
        <Field label="Scheduled at">
          <TextField
            value={toLocalInput(node.scheduled_at)}
            type="datetime-local"
            onSave={(v) => save({ scheduled_at: fromLocalInput(v) })}
          />
        </Field>
        <Field label="Duration (min)">
          <TextField
            value={node.duration_min}
            type="number"
            onSave={(v) => save({ duration_min: v === null ? null : Number(v) })}
          />
        </Field>
        {(node.type === 'routine' || node.recurrence) && (
          <Field label="Recurrence (RRULE)" wide>
            <TextField
              value={node.recurrence}
              placeholder="FREQ=DAILY"
              onSave={(v) => save({ recurrence: v })}
            />
          </Field>
        )}
      </Section>

      {node.type === 'result' && (
        <Section title="Result">
          <Field label="Completion criterion" wide>
            <TextField
              value={node.completion_criterion}
              multiline
              placeholder="The observable condition that counts as success"
              onSave={(v) => save({ completion_criterion: v })}
            />
          </Field>
          <Field label="Metric">
            <TextField value={node.success_metric} onSave={(v) => save({ success_metric: v })} />
          </Field>
          <Field label="Target / current">
            <div className="flex gap-1">
              <TextField
                value={node.target_value}
                type="number"
                placeholder="target"
                onSave={(v) => save({ target_value: v === null ? null : Number(v) })}
              />
              <TextField
                value={node.current_value}
                type="number"
                placeholder="current"
                onSave={(v) => save({ current_value: v === null ? null : Number(v) })}
              />
            </div>
          </Field>
          <Field label="Evidence" wide>
            <TextField value={node.evidence} multiline onSave={(v) => save({ evidence: v })} />
          </Field>
        </Section>
      )}

      <Section title="Assignment and buddy">
        <Field label="Assignee">
          <Select
            value={node.assignee_id ?? ''}
            onChange={(e) => save({ assignee_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Unassigned</option>
            {users
              ?.filter((u) => u.role !== 'system')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.kind === 'agent' ? '🤖 ' : ''}
                  {u.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Buddy focus">
          <label className="flex h-9 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={node.buddy_focus}
              onChange={(e) => buddy.mutate(e.target.checked)}
            />{' '}
            Agent pushes on this item
          </label>
        </Field>
        {node.buddy_focus && (
          <>
            <Field label="Check cadence">
              <Select
                value={node.check_cadence ?? ''}
                onChange={(e) =>
                  save({ check_cadence: (e.target.value || null) as ApiNode['check_cadence'] })
                }
              >
                <option value="">–</option>
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
                <option value="on_due">on due date</option>
              </Select>
            </Field>
            <Field label="Next check">
              <TextField
                value={toLocalInput(node.next_check_at)}
                type="datetime-local"
                onSave={(v) => save({ next_check_at: fromLocalInput(v) })}
              />
            </Field>
            <Field label="Buddy note (how to push)" wide>
              <TextField value={node.buddy_note} multiline onSave={(v) => save({ buddy_note: v })} />
            </Field>
          </>
        )}
      </Section>

      <Section title="Clarification">
        <Field label="Open question" wide>
          <TextField value={node.open_question} multiline onSave={(v) => save({ open_question: v })} />
        </Field>
        <Field label="Needs clarification">
          <label className="flex h-9 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={node.needs_clarification}
              onChange={(e) => save({ needs_clarification: e.target.checked })}
            />{' '}
            Agent should ask
          </label>
        </Field>
        <Field label="Waiting for">
          <TextField value={node.waiting_for} onSave={(v) => save({ waiting_for: v })} />
        </Field>
        <Field label="Blocked by (node id)">
          <TextField
            value={node.blocked_by_id}
            type="number"
            onSave={(v) => save({ blocked_by_id: v === null ? null : Number(v) })}
          />
        </Field>
      </Section>
      <div className="px-1 pt-1 text-xs text-muted-fg">
        Created {fmtDateTime(node.created_at)} · updated {fmtDateTime(node.updated_at)}
        {node.converted_from_type && ` · converted from ${node.converted_from_type}`}
        {node.completed_at && ` · done ${fmtDateTime(node.completed_at)}`}
      </div>
    </div>
  );
}

function TitleField({ node }: { node: ApiNode }) {
  const update = useUpdateNode();
  const [v, setV] = useState(node.title);
  const commit = () => {
    const t = v.trim();
    if (t && t !== node.title) update.mutate({ id: node.id, title: t });
    else setV(node.title);
  };
  return (
    <textarea
      value={v}
      rows={1}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className="w-full resize-none rounded-lg bg-transparent px-1 py-0.5 text-xl font-semibold leading-snug outline-none [field-sizing:content] hover:bg-muted/60 focus:bg-muted/60"
      aria-label="Title"
    />
  );
}

function QuickProps({ node }: { node: ApiNode }) {
  const update = useUpdateNode();
  const { data: users } = useUsers();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        className="h-8 w-auto text-[13px] font-medium"
        value={node.status}
        aria-label="Status"
        onChange={(e) => update.mutate({ id: node.id, status: e.target.value as ApiNode['status'] })}
      >
        {['idea', 'active', 'waiting', 'done', 'dropped'].map((s) => (
          <option key={s}>{s}</option>
        ))}
      </Select>
      <Input
        type="date"
        aria-label="Due date"
        className="h-8 w-auto text-[13px]"
        value={node.due_date ?? ''}
        onChange={(e) => update.mutate({ id: node.id, due_date: e.target.value || null })}
      />
      <Select
        className="h-8 w-auto text-[13px]"
        aria-label="Assignee"
        value={node.assignee_id ?? ''}
        onChange={(e) =>
          update.mutate({ id: node.id, assignee_id: e.target.value ? Number(e.target.value) : null })
        }
      >
        <option value="">Unassigned</option>
        {users
          ?.filter((u) => u.role !== 'system')
          .map((u) => (
            <option key={u.id} value={u.id}>
              {u.kind === 'agent' ? '🤖 ' : ''}
              {u.name}
            </option>
          ))}
      </Select>
    </div>
  );
}

export function DetailPanel() {
  const { openId, open } = useDetail();
  const { data: node, isLoading, error } = useNode(openId);
  const { data: structure } = useStructure();
  const done = useDone();
  const reopen = useReopen();
  const archive = useMutation({
    mutationFn: (id: number) => api.archiveNode(id),
    onSuccess: () => open(null),
  });
  const crumbs = node && structure ? ancestorsOf(structure, node.id) : [];
  const { data: ctx } = useQuery({
    queryKey: ['children-count', openId],
    queryFn: () => api.nodes({ parent: openId!, include_done: true }),
    enabled: openId !== null,
  });
  useEffect(() => {
    if (openId === null) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key === 'Escape' && !el.closest('[role="dialog"],[role="menu"],[role="listbox"]')) open(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId, open]);
  if (openId === null) return null;
  const isDone = node?.status === 'done';

  return (
    <aside className="fixed inset-y-0 right-0 z-30 flex w-full animate-slide-in flex-col overflow-hidden border-l border-border bg-surface shadow-[var(--shadow-pop)] sm:w-[460px] xl:static xl:z-auto xl:w-[500px] xl:shrink-0 xl:shadow-none">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-1.5 truncate text-xs text-muted-fg">
          {node && <TypeIcon type={node.type} size={13} />}
          <span className="truncate">
            {crumbs.map((c) => c.title).join(' › ') || (node ? node.type : '')}
          </span>
        </div>
        <div className="flex shrink-0 gap-0.5">
          {node && (
            <>
              <Link
                to={`/node/${node.id}?n=${node.id}`}
                title="Open as list"
                aria-label="Open as list"
                className={buttonVariants({ variant: 'ghost', size: 'icon' })}
              >
                <ExternalLink size={15} />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                title="Archive with children"
                aria-label="Archive"
                onClick={() =>
                  confirm(`Archive "${node.title}" and its ${ctx?.length ?? 0} children?`) &&
                  archive.mutate(node.id)
                }
              >
                <Archive size={15} />
              </Button>
            </>
          )}
          <Button variant="ghost" size="icon" onClick={() => open(null)} aria-label="Close">
            <X size={17} />
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading && <Spinner />}
        {error && <div className="text-sm text-danger">{String(error)}</div>}
        {node && (
          <div className="space-y-4">
            <div className="flex items-start gap-2.5">
              {['task', 'result', 'goal', 'project', 'idea'].includes(node.type) && (
                <button
                  className={`mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${isDone ? 'border-ok bg-ok text-white' : 'border-muted-fg/40 text-transparent hover:border-ok hover:text-ok/60'}`}
                  aria-label={isDone ? 'Mark as not done' : 'Mark as done'}
                  onClick={() => (isDone ? reopen.mutate(node.id) : done.mutate(node.id))}
                >
                  <Check size={14} strokeWidth={3} />
                </button>
              )}
              <div className="min-w-0 flex-1">
                <TitleField key={node.id + node.title} node={node} />
              </div>
            </div>
            <QuickProps key={node.id + node.updated_at} node={node} />
            <Tabs defaultValue="notes" key={node.id}>
              <TabsList className="mb-4">
                <TabsTrigger value="notes">Notes</TabsTrigger>
                {node.type === 'routine' && <TabsTrigger value="routine">Tracker</TabsTrigger>}
                <TabsTrigger value="fields">Details</TabsTrigger>
                <TabsTrigger value="agent">Agent notes</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>
              <TabsContent value="notes">
                <NoteEditor nodeId={node.id} kind="human" />
              </TabsContent>
              <TabsContent value="fields">
                <NodeForm node={node} />
              </TabsContent>
              {node.type === 'routine' && (
                <TabsContent value="routine">
                  <RoutineTracker routineId={node.id} />
                </TabsContent>
              )}
              <TabsContent value="agent">
                <NoteEditor nodeId={node.id} kind="agent" readOnly />
              </TabsContent>
              <TabsContent value="activity">
                <ActivityTimeline nodeId={node.id} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </aside>
  );
}
